import { CssBaseline, StyledEngineProvider, useMediaQuery } from '@mui/material';
import * as locales from '@mui/material/locale';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Font } from '@react-pdf/renderer';
import robotoBold from 'fonts/Roboto-Bold.ttf';
import robotoItalic from 'fonts/Roboto-Italic.ttf';
import robotoRegular from 'fonts/Roboto-Regular.ttf';
import robotoThin from 'fonts/Roboto-Thin.ttf';
import { SnackbarProvider } from 'notistack';
import React, { useEffect, useMemo, Suspense } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { NavBar, PrivateRoute } from './components';
import useDarkModeState from './hooks/useDarkMode';
import { useLanguageState } from './hooks/useLanguageState';
import {
  bargeTripsPath,
  bargesPath,
  businessUnitsPath,
  calendarPath,
  changePasswordPath,
  configurationsPath,
  counterPartiesPath,
  dashboardPath,
  dataMaintenancePath,
  dealPath,
  dealPortFormPath,
  forgotPasswordPath,
  integrationsPath,
  interruptionDefinitionsPath,
  invitationPath,
  laycanCalculationPath,
  laytimeChartPath,
  laytimePerCounterpartyPath,
  maritimeDirectoriesPath,
  ocrPath,
  ocrWizardPath,
  portsPath,
  portsReportPath,
  productPath,
  profitAndLossPath,
  publicLaytimeChart,
  quotePath,
  registerNewTenantPath,
  reportsPath,
  signInPath,
  statementOfFactPath,
  supplierVsOwnerPath,
  tradersPath,
  userPath,
  userProfilePath,
  vesselPath,
  voyageProfitAndLossPath
} from './shared/links';

import { TXT_DOWNLOAD_LAYTIME_CHART } from '../../../shared/translations';

const DataMaintenance = React.lazy(() => import('pages/DataMaintenance/DataMaintenance'));
const BargeTrips = React.lazy(() => import('./pages').then((module) => ({ default: module.BargeTrips })));
const Barges = React.lazy(() => import('./pages').then((module) => ({ default: module.Barges })));
const BusinessUnits = React.lazy(() => import('./pages').then((module) => ({ default: module.BusinessUnits })));
const Calendar = React.lazy(() => import('./pages').then((module) => ({ default: module.Calendar })));
const ChangePassword = React.lazy(() => import('./pages').then((module) => ({ default: module.ChangePassword })));
const Configurations = React.lazy(() => import('./pages').then((module) => ({ default: module.Configurations })));
const CounterPartyPage = React.lazy(() => import('./pages').then((module) => ({ default: module.CounterPartyPage })));
const Dashboard = React.lazy(() => import('./pages').then((module) => ({ default: module.Dashboard })));
const DealPortForm = React.lazy(() => import('./pages').then((module) => ({ default: module.DealPortForm })));
const Deals = React.lazy(() => import('./pages').then((module) => ({ default: module.Deals })));
const DemurragePage = React.lazy(() => import('./pages').then((module) => ({ default: module.DemurragePage })));
const ForgotPassword = React.lazy(() => import('./pages').then((module) => ({ default: module.ForgotPassword })));
const Integrations = React.lazy(() => import('./pages').then((module) => ({ default: module.Integrations })));
const InterruptionDefinitions = React.lazy(() => import('./pages').then((module) => ({ default: module.InterruptionDefinitions })));
const Invitation = React.lazy(() => import('./pages').then((module) => ({ default: module.Invitation })));
const LaytimeChart = React.lazy(() => import('./pages').then((module) => ({ default: module.LaytimeChart })));
const LaytimePerCounterparty = React.lazy(() => import('./pages').then((module) => ({ default: module.LaytimePerCounterparty })));
const MaritimeDirectories = React.lazy(() => import('./pages').then((module) => ({ default: module.MaritimeDirectories })));
const OcrScan = React.lazy(() => import('./pages').then((module) => ({ default: module.OcrScan })));
const OcrScanWizard = React.lazy(() => import('./pages').then((module) => ({ default: module.OcrScanWizard })));
const Ports = React.lazy(() => import('./pages').then((module) => ({ default: module.Ports })));
const Products = React.lazy(() => import('./pages').then((module) => ({ default: module.Products })));
const ProfitAndLoss = React.lazy(() => import('./pages').then((module) => ({ default: module.ProfitAndLoss })));
const PublicLaytimeChart = React.lazy(() => import('./pages').then((module) => ({ default: module.PublicLaytimeChart })));
const RegisterTenant = React.lazy(() => import('./pages').then((module) => ({ default: module.RegisterTenant })));
const Reports = React.lazy(() => import('./pages').then((module) => ({ default: module.Reports })));
const SignIn = React.lazy(() => import('./pages').then((module) => ({ default: module.SignIn })));
const StatementOfFacts = React.lazy(() => import('./pages').then((module) => ({ default: module.StatementOfFacts })));
const SupplierVsOwner = React.lazy(() => import('./pages').then((module) => ({ default: module.SupplierVsOwner })));
const Traders = React.lazy(() => import('./pages').then((module) => ({ default: module.Traders })));
const Users = React.lazy(() => import('./pages').then((module) => ({ default: module.Users })));
const Vessels = React.lazy(() => import('./pages').then((module) => ({ default: module.Vessels })));
const PortsReport = React.lazy(() => import('./pages').then((module) => ({ default: module.PortsReport })));
const Quotes = React.lazy(() => import('./pages/Quote').then((module) => ({ default: module.Quotes })));
const UserProfile = React.lazy(() => import('./pages/User/UserProfile').then((module) => ({ default: module.UserProfile })));
const VoyageProfitAndLoss = React.lazy(() => import('./pages/VoyageProfitAndLoss').then((module) => ({ default: module.VoyageProfitAndLoss })));


type SupportedLocales = keyof typeof locales;

Font.register({
  family: 'Roboto',
  fonts: [
    {
      src: robotoRegular
    },
    {
      src: robotoBold,
      fontWeight: 'bold'
    },
    {
      src: robotoThin,
      fontWeight: 'thin'
    },
    {
      src: robotoItalic,
      fontStyle: 'italic'
    }
  ]
});

const App = () => {
  const [darkMode, setDarkMode] = useDarkModeState();
  const systemDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const [language] = useLanguageState();

  useEffect(() => {
    if (darkMode === undefined) {
      setDarkMode(systemDarkMode);
    }
  }, [systemDarkMode]);

  useEffect(() => {
    const body = document.body;
    if (darkMode) {
      body.classList.add('dark');
      body.classList.add('e-dark-mode');
    } else {
      body.classList.remove('dark');
      body.classList.remove('e-dark-mode');
    }
  }, [darkMode]);

  const theme = useMemo(
    () =>
      createTheme(
        {
          palette: {
            mode: darkMode ? 'dark' : 'light'
          }
        },
        locales[language as SupportedLocales]
      ),
    [darkMode, language]
  );
  return (
    <StyledEngineProvider injectFirst>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <BrowserRouter>
            <SnackbarProvider>
              <NavBar>
                <Suspense fallback={<div>Loading...</div>}>
                  <Routes>
                    <Route path={signInPath} element={<SignIn />} />
                    <Route path={invitationPath} element={<Invitation />} />
                    <Route path={registerNewTenantPath} element={<RegisterTenant />} />
                    <Route
                      path={publicLaytimeChart}
                      element={<PublicLaytimeChart title={TXT_DOWNLOAD_LAYTIME_CHART} />}
                    />
                    <Route path={forgotPasswordPath} element={<ForgotPassword />} />
                    <Route path={changePasswordPath} element={<ChangePassword />} />
                    <Route path={`${dealPortFormPath}/:linkId`} element={<DealPortForm />} />
                    <Route path="/" element={<PrivateRoute />}>
                      <Route path={dashboardPath} element={<Dashboard />} />
                      <Route path={vesselPath} element={<Vessels />} />
                      <Route path={counterPartiesPath} element={<CounterPartyPage />} />
                      <Route path={userPath} element={<Users />} />
                      <Route path={`${laycanCalculationPath}/:dealId`} element={<DemurragePage />} />
                      <Route path={dealPath} element={<Deals />} />
                      <Route path={quotePath} element={<Quotes />} />
                      <Route path={portsPath} element={<Ports />} />
                      <Route path={productPath} element={<Products />} />
                      <Route path={interruptionDefinitionsPath} element={<InterruptionDefinitions />} />
                      <Route path={reportsPath} element={<Reports />} />
                      <Route path={integrationsPath} element={<Integrations />} />
                      <Route path={tradersPath} element={<Traders />} />
                      <Route path={businessUnitsPath} element={<BusinessUnits />} />
                      <Route path={configurationsPath} element={<Configurations />} />
                      <Route path={userProfilePath} element={<UserProfile />} />
                      <Route path={profitAndLossPath} element={<ProfitAndLoss />} />
                      <Route path={voyageProfitAndLossPath} element={<VoyageProfitAndLoss />} />
                      <Route path={bargesPath} element={<Barges />} />
                      <Route path={bargeTripsPath} element={<BargeTrips />} />
                      <Route path={calendarPath} element={<Calendar />} />
                      <Route path={`${ocrPath}/:statementOfFactId`} element={<OcrScan />} />
                      <Route path={ocrPath} element={<OcrScan />} />
                      <Route path={`${ocrWizardPath}/:statementOfFactId`} element={<OcrScanWizard />} />
                      <Route path={ocrWizardPath} element={<OcrScanWizard />} />
                      <Route path={statementOfFactPath} element={<StatementOfFacts />} />
                      <Route path={maritimeDirectoriesPath} element={<MaritimeDirectories />} />
                      <Route path={dataMaintenancePath} element={<DataMaintenance />} />
                      <Route path={laytimePerCounterpartyPath} element={<LaytimePerCounterparty />} />
                      <Route path={supplierVsOwnerPath} element={<SupplierVsOwner />} />
                      <Route path={laytimeChartPath} element={<LaytimeChart />} />
                      <Route path={portsReportPath} element={<PortsReport />} />
                    </Route>
                  </Routes>
                </Suspense>
              </NavBar>
            </SnackbarProvider>
          </BrowserRouter>
        </ThemeProvider>
      </LocalizationProvider>
    </StyledEngineProvider>
  );
};

export default App;
